import { useState, useEffect, useCallback } from 'react';
import { Loader } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@components/common/Button';
import { useOnboardingConfig } from '@api/hooks/useOnboarding';
import { useUpdateFieldConfig } from '@api/hooks/useUpdateFieldConfig';
import { ExtractionFieldsStep } from '@components/Onboarding/steps/ExtractionFieldsStep';
import { VendorFieldsStep } from '@components/Onboarding/steps/VendorFieldsStep';
import { BillCreationFieldsStep } from '@components/Onboarding/steps/BillCreationFieldsStep';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import type { OnboardingConfigResponse } from '@api/hooks/useOnboarding';

export const FieldConfigurationTab = () => {
  const { data: config, isLoading, error } = useOnboardingConfig();
  const updateConfig = useUpdateFieldConfig();

  // Local state for edits
  const [extractionFields, setExtractionFields] = useState<Record<string, string[]>>({});
  const [vendorFields, setVendorFields] = useState<string[]>([]);
  const [billFields, setBillFields] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Helper: Sync config to local state
  const syncConfigToState = useCallback((cfg: OnboardingConfigResponse | undefined) => {
    if (cfg) {
      setExtractionFields(cfg.extraction_fields);
      setVendorFields(cfg.vendor_fields);
      setBillFields(cfg.bill_creation_fields);
      setHasChanges(false);
    }
  }, []);

  // Sync loaded config to local state
  useEffect(() => {
    syncConfigToState(config);
  }, [config, syncConfigToState]);

  // Track changes
  const handleExtractionFieldsChange = (fields: Record<string, string[]>) => {
    setExtractionFields(fields);
    setHasChanges(true);
  };

  const handleVendorFieldsChange = (fields: string[]) => {
    setVendorFields(fields);
    setHasChanges(true);
  };

  const handleBillFieldsChange = (fields: string[]) => {
    setBillFields(fields);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!config) return; // Safety check

    try {
      await updateConfig.mutateAsync({
        extraction_fields: extractionFields as {
          purchase_order: string[];
          receipt: string[];
          invoice: string[];
        },
        vendor_fields: vendorFields,
        bill_creation_fields: billFields,
      });
      setHasChanges(false);
    } catch {
      // Error handled by hook's onError
    }
  };

  const handleCancel = () => {
    syncConfigToState(config);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-4">
        <p className="text-red-600 dark:text-red-400">
          Failed to load field configuration. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Document Extraction Fields */}
      <div className={clsx(
        'rounded-lg border p-6',
        'bg-white dark:bg-gray-900',
        'border-gray-200 dark:border-gray-800',
        'shadow-sm'
      )}>
        <ExtractionFieldsStep
          extractionFields={extractionFields}
          onChange={handleExtractionFieldsChange}
        />
      </div>

      {/* Vendor Fields */}
      <div className={clsx(
        'rounded-lg border p-6',
        'bg-white dark:bg-gray-900',
        'border-gray-200 dark:border-gray-800',
        'shadow-sm'
      )}>
        <VendorFieldsStep
          selectedFields={vendorFields}
          onChange={handleVendorFieldsChange}
        />
      </div>

      {/* Bill Creation Fields */}
      <div className={clsx(
        'rounded-lg border p-6',
        'bg-white dark:bg-gray-900',
        'border-gray-200 dark:border-gray-800',
        'shadow-sm'
      )}>
        <BillCreationFieldsStep
          selectedFields={billFields}
          onChange={handleBillFieldsChange}
        />
      </div>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateConfig.isPending || !config}
          >
            {updateConfig.isPending && <Loader size={16} className="animate-spin mr-2" />}
            {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={updateConfig.isPending || !config}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};
